import { createFileRoute, Link } from "@tanstack/react-router";
import { ctrDesc, ctrTitle } from "@/lib/seo-copy";
import { SWORN_COMPARE } from "@/data/sworn/compare";
import { absoluteUrl } from "@/config/sites";
import { Breadcrumbs } from "@/components/Breadcrumbs";

const TITLE = "เทียบทางเลือกแปลสาบานตนสำหรับยื่นสถานทูต";
const DESC =
  "เลือกชั้นการรับรองคำแปลให้ถูกตั้งแต่ครั้งแรก เทียบทีละข้อระหว่างนักแปลที่สถานทูตยอมรับ นักแปลขึ้นทะเบียนกระทรวงยุติธรรม ทนายความผู้ทำคำรับรอง และนิติกรณ์กรมการกงสุล สอบถามทีมงานได้ทาง LINE";

export const Route = createFileRoute("/sworn/compare/")({
  loader: () => {
    const url = absoluteUrl("/sworn/compare");
    return {
      h: {
        meta: [
          { title: ctrTitle(TITLE) },
          { name: "description", content: ctrDesc(DESC) },
          { property: "og:title", content: ctrTitle(TITLE) },
          { property: "og:description", content: ctrDesc(DESC) },
          { property: "og:url", content: url },
          { property: "og:type", content: "website" },
          { name: "twitter:card", content: "summary" },
        ],
        links: [{ rel: "canonical", href: url }],
        scripts: [
          {
            type: "application/ld+json",
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "ItemList",
              name: TITLE,
              itemListElement: SWORN_COMPARE.map((c, i) => ({
                "@type": "ListItem",
                position: i + 1,
                name: c.h1,
                url: absoluteUrl(`/sworn/compare/${c.slug}`),
              })),
            }),
          },
        ],
      },
    };
  },
  head: ({ loaderData }) => loaderData?.h ?? {},
  component: SwornCompareHub,
});

function SwornCompareHub() {
  return (
    <>
      <Breadcrumbs jsonLd
        items={[
          { label: "หน้าแรก", to: "/" },
          { label: "นักแปลสาบานตน", to: "/sworn" },
          { label: "เทียบทางเลือก" },
        ]}
      />
      <section className="mx-auto max-w-3xl px-4 py-10">
        <h1 className="text-3xl font-bold mb-5">{TITLE}</h1>
        <p className="answer-summary text-lg mb-8 p-4 bg-muted rounded-xl" data-speakable>
          คำแปล การรับรองลายมือชื่อ และการรับรองเอกสารเพื่อใช้ต่างประเทศ เป็นสามเรื่องที่ทำหน้าที่ต่างกัน
          ปลายทางเป็นผู้กำหนดว่าต้องใช้ชั้นใดและเรียงลำดับอย่างไร หน้าด้านล่างเทียบทีละข้อ
          พร้อมค่าธรรมเนียมราชการที่อ้างอิงได้ เพื่อให้จัดชุดเอกสารครบในรอบเดียว
        </p>

        <ul className="space-y-4">
          {SWORN_COMPARE.map((c) => (
            <li key={c.slug} className="border rounded-xl p-5">
              <h2 className="font-semibold mb-1">
                <Link to="/sworn/compare/$slug" params={{ slug: c.slug }} className="text-primary hover:underline">
                  {c.h1}
                </Link>
              </h2>
              <p className="text-sm text-muted-foreground">{c.description}</p>
            </li>
          ))}
        </ul>

        <p className="mt-8 text-sm">
          <Link to="/sworn/questions" className="text-primary hover:underline">
            คำถามที่พบบ่อยเรื่องแปลสาบานตน
          </Link>
          {" · "}
          <Link to="/moj/compare" className="text-primary hover:underline">
            เทียบทางเลือกแปลรับรองนักแปลกระทรวงยุติธรรม
          </Link>
        </p>

        <p className="mt-6 text-xs text-muted-foreground">
          ไม่แสดงราคาบริการบนหน้าเว็บ — สอบถามค่าบริการทางโทรศัพท์ LINE หรืออีเมล
          ค่าธรรมเนียมราชการเป็นอัตราของหน่วยงานและแสดงแยกจากค่าบริการเสมอ
        </p>
      </section>
    </>
  );
}
